import { Injectable, NgZone } from '@angular/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { AppLauncher } from '@capacitor/app-launcher';
import { Preferences } from '@capacitor/preferences';
import { environment } from 'src/environments/environment';
import { AccountService } from './account.service';
import * as Sentry from "@sentry/angular";
import { firstValueFrom, Subject, take } from 'rxjs';
import { CourierRepo } from '../repositories/courier.repo';

interface VivaBranchConfig {
    apiKey: string;
    apiSecret: string;
    sourceIdCode: string;
    pinCode: string;
}

@Injectable({ providedIn: 'root' })
export class VivaWalletPaymentService {
    private readonly callbackScheme = 'deliverymaster://payment/result';
    private readonly baseScheme = 'vivapayclient://pay/v1';
    private readonly posActivatedKey = 'posActivated';
    private saleResultSubject = new Subject<{ orderId: string; status: string }>();
    private isTryingRecovery = false;

    private posActivationComplete$ = new Subject<void>();
    public saleResult$ = this.saleResultSubject.asObservable();

    constructor(
        private zone: NgZone,
        private accountService: AccountService,
        private courierRepo: CourierRepo
    ) {
        this.initializeDeepLinkListener();
    }

    /**
     * Combined initialization: set terminal to Semi-Unattended,
     * then automatically activate the POS for this branch.
     */
    async initializeTerminal(branchId?: string): Promise<void> {
        const activated = await this.isPosActivated();
        if (activated) {
            return;
        }

        if (!branchId) {
            const branch = await firstValueFrom(this.accountService.getBranch());
            branchId = branch.id;
        }

        const { pinCode } = this.getBranchConfig(branchId);
        const { id: appId } = await App.getInfo();
        const params = new URLSearchParams({
            appId,
            action: 'setMode',
            mode: '1',
            pin: pinCode,
            callback: this.callbackScheme,
        });

        await this.sendIntent(params);
    }

    /**
     * Start a Tap-on-Phone sale
     */
    async startPayment(amountCents: number, orderId: string): Promise<void> {
        console.log('[VivaWallet] Entering startPayment');
        const { id: appId } = await App.getInfo();
        console.log('[VivaWallet] App ID:', appId, 'Order ID:', orderId, 'Amount:', amountCents);
        const params = new URLSearchParams({
            appId,
            action: 'sale',
            clientTransactionId: orderId,
            amount: amountCents.toFixed(0),
            callback: this.callbackScheme,
            show_receipt: 'false',
            show_transaction_result: 'true',
        });

        console.log('[VivaWallet] Sending intent for sale with params:', params.toString());
        await this.sendIntent(params);
    }

    /**
     * Soft or full reset of the Viva Terminal
     */
    async resetTerminal(softReset: boolean = true): Promise<void> {
        const { id: appId } = await App.getInfo();
        const params = new URLSearchParams({
            merchantKey: 'deprecated',
            appId,
            action: 'reset',
            softReset: softReset.toString(),
            callback: this.callbackScheme,
        });
        await this.sendIntent(params);

        if (!softReset) {
            await this.clearPosActivated();
        }
    }

    /**
     * Listen for callbacks: setMode → activatePos → sale → reset
     */
    private initializeDeepLinkListener(): void {
        App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {

            if (!event.url.startsWith(this.callbackScheme)) {
                return;
            }

            this.zone.run(async () => {
                try {
                    const url = new URL(event.url);
                    const action = url.searchParams.get('action');
                    const status = url.searchParams.get('status');
                    const msg = url.searchParams.get('message');

                    switch (action) {
                        case 'setMode':
                            if (status === 'success') {
                                console.log('[VivaWallet] setMode succeeded. Proceeding to activate POS.');
                                await this.activatePosOnly();
                            } else {
                                console.error('[VivaWallet] setMode failed:', msg);
                            }
                            break;

                        case 'activatePos':
                            console.log('[VivaWallet] Handling callback action: activatePos');
                            if (status === 'success') {
                                console.log('[VivaWallet] POS activation succeeded:', msg);
                                await this.markPosActivated();
                                this.posActivationComplete$.next();
                            } else {
                                console.warn('[VivaWallet] POS activation failed:', msg);
                                if (msg?.includes('POS_IS_ALREADY_ACTIVATED')) {
                                    console.log('[VivaWallet] POS already activated:', msg);
                                    await this.markPosActivated();
                                }
                            }
                            break;

                        case 'sale':
                            await this.handleSaleCallback(url);
                            break;

                        case 'reset':
                            console.log('[VivaWallet] Terminal reset:', { status, msg });
                            break;

                        default:
                            console.warn('[VivaWallet] Unknown callback action received:', action);
                            break;
                    }
                } catch (error) {
                    console.error('[VivaWallet] Error while handling deep link callback:', error);
                    Sentry.captureException(error);
                }
            });
        });
    }

    /** Internal: send the Activate POS intent */
    private async activatePosOnly(): Promise<void> {
        const branchId = (await firstValueFrom(this.accountService.getBranch())).id;
        const { apiKey, apiSecret, sourceIdCode, pinCode } = this.getBranchConfig(branchId);
        const { id: appId } = await App.getInfo();
        const params = new URLSearchParams({
            appId,
            action: 'activatePos',
            apikey: apiKey,
            apiSecret: apiSecret,
            callback: this.callbackScheme,
            sourceID: sourceIdCode,
            pinCode: pinCode,
            skipExternalDeviceSetup: 'true',
            activateQRCodes: 'false',
            disableManualAmountEntry: 'false',
            forceCardPresentmentForRefund: 'true',
            lockRefund: 'true',
            lockTransactionsList: 'true',
        });
        await this.sendIntent(params);
    }

    private async handleSaleCallback(url: URL): Promise<void> {
        const status = url.searchParams.get('status');
        const message = url.searchParams.get('message');
        const orderId = url.searchParams.get('clientTransactionId');
        const amount = url.searchParams.get('amount');

        if (status === 'fail' && message?.includes('APPLICATION_NOT_LOGGED_IN')) {
            console.warn('Detected not logged in, attempting recovery...');
            if (!this.isTryingRecovery) {
                this.isTryingRecovery = true;

                const branch = await firstValueFrom(this.accountService.getBranch())
                await this.initializeTerminal(branch.id);

                this.posActivationComplete$.pipe(take(1)).subscribe(async () => {
                    await this.startPayment(Number(amount), orderId || '');
                    this.isTryingRecovery = false;
                });
            }
            return;
        }

        if (status === 'success') {
            console.log('✅ Sale succeeded:', orderId);
            if (!orderId) return;
            this.courierRepo.setOrderAsPaid((orderId)).subscribe();
            this.saleResultSubject.next({ orderId: orderId, status });
        } else {
            console.warn('❌ Sale failed:', message);
            Sentry.captureMessage(`Viva sale failed: ${message || 'Unknown reason'}`, {
                level: 'error',
                tags: {
                    component: 'VivaWalletPaymentService',
                    orderId: orderId ?? 'unknown',
                },
                extra: {
                    url: url.toString(),
                    status,
                    message,
                    orderId,
                    amount,
                },
            });

            if (orderId) {
                this.saleResultSubject.next({ orderId: orderId, status: 'failed' });
            }
        }
    }

    /** Check local storage for activation flag */
    async isPosActivated(): Promise<boolean> {
        const { value } = await Preferences.get({ key: this.posActivatedKey });
        return value === 'true';
    }

    private async markPosActivated(): Promise<void> {
        await Preferences.set({ key: this.posActivatedKey, value: 'true' });
    }

    private async clearPosActivated(): Promise<void> {
        await Preferences.remove({ key: this.posActivatedKey });
    }

    /** Fire any Viva intent via Android ACTION_VIEW */
    private async sendIntent(params: URLSearchParams): Promise<void> {
        await AppLauncher.openUrl({ url: `${this.baseScheme}?${params.toString()}` });
    }

    /** Load per-branch credentials from environment */
    private getBranchConfig(branchId: string): VivaBranchConfig {
        const cfg = environment.vivaBranches[branchId];
        if (!cfg) throw new Error(`No Viva config for branch with id "${branchId}"`);
        return cfg;
    }
}
